<template>
  <main class="page no-tabs">
    <page-header :title="creating ? '新增客户' : '写跟进'" back @back="$router.back()">
      <span v-if="ready" class="draft-line" aria-live="polite"><app-icon :name="draftOk ? 'check' : 'close'" />{{ draftShort }}</span>
    </page-header>
    <div v-if="loading" class="loading-state">
      <span class="spinner" />
      正在读取客户资料
    </div>
    <div v-if="loadError" class="empty-state">
      <strong>资料加载失败</strong>
      <p>{{ loadError }}</p>
      <button class="button small soft" @click="load">重新加载</button>
    </div>
    <template v-if="ready">
      <div class="who-strip">
        <div v-if="creating">
          <b>{{ info.mobile }}</b>
          <span>新客户 · 先记下这次沟通，再补资料</span>
        </div>
        <div v-else>
          <b>{{ info.name || '未填写姓名' }}</b>
          <span>{{ whoLine }}</span>
        </div>
        <a v-if="callable" :href="'tel:' + info.mobile" class="c-dial" aria-label="拨打客户电话">
          <app-icon name="phone" />
        </a>
      </div>
      <div v-if="draftRestored" class="notice">
        已恢复上次未保存的草稿
        <button type="button" class="text-button" @click="discardDraft">丢弃草稿</button>
      </div>
      <div v-if="draftConflict" class="notice">客户资料已有更新，已保留草稿文字，请核对本次修改。</div>
      <follow-up-editor v-model="content" :customer-id="creating ? null : info.id" :draft-key="creating ? key : ''" :profile="analysisProfile" :profile-values="info" :profile-choices="profileChoices" :disabled="saving" allow-keywords @organized="applyKeyword" @schedule="applyAiTime" @profile="applyAiProfile" />
      <div v-if="profileUndoCount" class="notice">已填入 {{ profileUndoCount }} 项画像，保存后生效 <button class="text-button" type="button" :disabled="saving" @click="undoAiProfile">撤销画像填入</button></div>
      <p v-if="incomingLoading" class="notice" role="status">正在读取已选择的画像建议</p>
      <div v-if="incomingError" class="error-message" role="alert">{{ incomingError }} <button type="button" class="text-button" @click="loadIncomingProfile">重试读取</button></div>
      <section v-if="incomingProfile" class="group ai-incoming-profile">
        <customer-ai-profile :result="incomingProfile" :values="info" :choices="profileChoices" :initial-fields="incomingFields" :only-fields="incomingFields" :stale="!!incomingProfile.stale" :disabled="saving" @apply="applyAiProfile" />
        <button type="button" class="text-button" @click="incomingProfile = null">暂不采用</button>
      </section>
      <p v-if="!draftOk" class="error-message">{{ draftStatus }}</p>

      <section class="group">
        <div class="fr stack">
          <span class="fr-label req">客户状态</span>
          <div class="seg">
            <button v-for="item in statuses" :key="item.key" type="button" :class="{ on: Number(info.status) === Number(item.key) }" :disabled="saving" @click="info.status = Number(item.key)">
              {{ item.value }}
            </button>
          </div>
        </div>
        <div class="fr stack">
          <span class="fr-label req">意向等级</span>
          <div class="seg">
            <button v-for="item in list('labels')" :key="item.key" type="button" :class="{ on: Number(info.labels) === Number(item.key) }" :disabled="saving" @click="info.labels = Number(item.key)">
              {{ item.value }}
            </button>
          </div>
        </div>
      </section>

      <section class="group">
        <div class="fr stack">
          <div class="fr-line">
            <span class="fr-label req">下次跟进</span>
            <button type="button" class="when-pill" :class="{ ph: !nextTime }" :disabled="saving" @click="nextOpen = true">
              {{ nextTime ? whenText(nextTime) : '选择时间' }}
              <app-icon name="arrow" />
            </button>
          </div>
          <div class="mini-chips">
            <button v-for="item in nextQuick" :key="item.label" type="button" :class="{ on: nextTime === item.value }" :disabled="saving" @click="nextTime = item.value">
              {{ item.label }}
            </button>
          </div>
        </div>
        <datetime-field v-model="info.come_time" label="预计来访" clearable placeholder="未约" :disabled="saving" />
      </section>
      <datetime-sheet :show="nextOpen" title="下次跟进" :value="nextTime" with-time quick :min="todayText" @input="nextTime = $event" @close="nextOpen = false" />

      <section class="group">
        <h3>客户资料</h3>
        <label v-if="creating" class="fr">
          <span class="fr-label">手机号</span>
          <input v-model="info.mobile" type="tel" readonly />
        </label>
        <label class="fr">
          <span class="fr-label req">称呼</span>
          <input v-model="info.name" :disabled="saving" placeholder="怎么称呼客户" maxlength="50" />
        </label>
        <select-field v-if="creating" v-model="info.source_channel_v2" label="客户来源" required :options="channelOptions" :disabled="saving" />
        <select-field v-model="info.total_budget_range" label="总价预算范围" multiple required :options="fieldOptions('total_budget_range')" :disabled="saving" />
        <select-field v-model="info.price_acceptance_level" label="单价接受程度" :clearable="false" :options="fieldOptions('price_acceptance_level')" :disabled="saving" />
        <select-field v-model="info.purpose_v2" label="用途" :clearable="false" :options="fieldOptions('purpose_v2')" :disabled="saving" />
        <label class="fr">
          <span class="fr-label req">关键词</span>
          <input v-model="info.keyword" :disabled="saving" maxlength="250" placeholder="如：餐饮 150平 蜀山" />
        </label>
      </section>

      <section class="group">
        <h3>需求</h3>
        <select-field v-for="field in mainNeeds" :key="field" :value="info[field]" :label="fieldLabel(field)" :options="fieldOptions(field)" :disabled="saving" @input="$set(info, field, $event)" />
        <button type="button" class="fold" :aria-expanded="more ? 'true' : 'false'" @click="more = !more">
          更多资料
          <span>{{ moreSummary }}<app-icon name="arrow" /></span>
        </button>
        <div v-show="more" class="fold-body">
          <label class="fr">
            <span class="fr-label">微信号</span>
            <input v-model="info.wechat" :disabled="saving" placeholder="选填" maxlength="50" />
          </label>
          <label class="fr">
            <span class="fr-label">微信昵称</span>
            <input v-model="info.wechat_nickname" :disabled="saving" placeholder="选填" maxlength="50" />
          </label>
          <select-field v-for="field in moreNeeds" :key="field" :value="info[field]" :label="fieldLabel(field)" :options="fieldOptions(field)" :disabled="saving" @input="$set(info, field, $event)" />
        </div>
      </section>
      <p v-if="error" class="error-message form-error" role="alert">{{ error }}</p>
      <div class="dock">
        <div class="dock-inner single">
          <button class="button" type="button" :disabled="saving" @click="save">
            <span v-if="saving" class="spinner" />
            <app-icon v-else name="check" />
            {{ saveText }}
          </button>
        </div>
      </div>
    </template>
  </main>
</template>
<script>
import FollowUpEditor from './follow-up-editor'
import SelectField from './select-field'
import DatetimeField from './datetime-field'
import DatetimeSheet from './datetime-sheet'
import CustomerAiProfile from './customer-ai-profile'
import { profileFields, sameProfileValue, profileValue } from '../common/ai-profile'
import { employee } from '../common/session'
import { validDay, scheduleLocation } from '../common/schedule'
import { customerFields, payload, inputDate, canCall, budgetValues } from '../common/customer'
import { aiProfile, validAiTime } from '../common/ai'
import {
  addDays,
  dateTimeText,
  needText,
  projectText,
  projectType,
  tail,
  today
} from '../common/when'
import { draftKey, readDraft, writeDraft, removeDraft } from '../common/draft'
import { returnToCustomerOrigin } from '../common/customer-return'
const typeOrder = ['住宅', '公寓', '商铺', '门面', '写字楼']
export default {
  components: { FollowUpEditor, SelectField, DatetimeField, DatetimeSheet, CustomerAiProfile },
  props: { mode: String },
  data() {
    return {
      info: {},
      options: {},
      content: '',
      nextTime: '',
      nextOpen: false,
      profileUndo: null,
      incomingProfile: null,
      incomingFields: [],
      incomingLoading: false,
      incomingError: '',
      todayText: today(),
      loading: true,
      ready: false,
      loadError: '',
      saving: false,
      error: '',
      more: false,
      baseline: null,
      key: '',
      draftRestored: false,
      draftConflict: false,
      draftStatus: '填写内容会自动保留为草稿',
      committed: false,
      mainNeeds: ['type', 'region', 'area'],
      moreNeeds: [
        'total',
        'price',
        'buy_type',
        'income',
        'manager',
        'industry',
        'job_time',
        'job',
        'buy_house',
        'financial',
        'introduce'
      ]
    }
  },
  computed: {
    profileChoices() {
      const result = {}
      profileFields.forEach(field => { result[field] = { label: this.fieldLabel(field), options: this.fieldOptions(field).map(item => ({ value: item.value, label: item.label })) } })
      return result
    },
    profileUndoCount() {
      return this.profileUndo ? Object.keys(this.profileUndo.after).filter(field => sameProfileValue(this.info[field], this.profileUndo.after[field])).length : 0
    },
    analysisProfile() {
      const current = aiProfile(this.info)
      if (this.creating) return current
      if (!this.baseline) return {}
      const changes = {}
      Object.keys(current).forEach(field => {
        if (field === 'log_time') return
        if (JSON.stringify(this.info[field]) !== JSON.stringify(this.baseline.fields[field])) changes[field] = current[field]
      })
      if (this.nextTime !== this.baseline.nextTime) changes.log_time = this.nextTime.replace('T', ' ')
      return changes
    },
    creating() {
      return this.mode === 'create'
    },
    callable() {
      return canCall(this.info.mobile)
    },
    statuses() {
      const values = this.list('status').slice()
      if (!this.creating && this.baseline && Number(this.baseline.fields.status) === 4)
        return [{ key: 4, value: '来访' }]
      if (Number(this.info.status) === 2 && !values.some(item => Number(item.key) === 2))
        values.push({ key: 2, value: '成交' })
      return values
    },
    dirty() {
      return (
        this.ready &&
        !this.committed &&
        JSON.stringify(this.snapshot()) !== JSON.stringify(this.baseline)
      )
    },
    whoLine() {
      const c = this.info
      return [needText(c), tail(c.mobile), c.labels_text ? c.labels_text + '级' : '', c.status_text]
        .filter(Boolean)
        .join(' · ')
    },
    draftOk() {
      return this.draftStatus.indexOf('无法') < 0
    },
    draftShort() {
      if (!this.draftOk) return '草稿未保存'
      return this.draftStatus.indexOf('已') >= 0 ? '草稿已存' : '自动存草稿'
    },
    saveText() {
      if (this.saving) return '正在保存'
      if (this.creating) return '创建客户并保存跟进'
      return this.$route.params.from === 'workbench' ? '保存，下一位' : '保存跟进'
    },
    nextQuick() {
      const base = this.todayText
      const items = [
        { label: '明天 10:00', value: addDays(base, 1) + 'T10:00' },
        { label: '3 天后', value: addDays(base, 3) + 'T10:00' },
        { label: '一周后', value: addDays(base, 7) + 'T10:00' }
      ]
      if (this.info.come_time)
        items.push({ label: '与来访同时', value: String(this.info.come_time).slice(0, 16) })
      return items
    },
    channelOptions() {
      return this.fieldOptions('source_channel_v2').filter(item => Number(item.value) !== 0)
    },
    subjectOptions() {
      const items = this.list('subject').map(item => {
        const type = projectType(item.text)
        return {
          value: Number(item.value),
          label: projectText(item.text) || item.text,
          hint: type,
          group: typeOrder.indexOf(type) >= 0 ? type : '其他',
          rank: typeOrder.indexOf(type) >= 0 ? typeOrder.indexOf(type) : typeOrder.length
        }
      })
      return items.sort((a, b) => a.rank - b.rank)
    },
    moreSummary() {
      const fields = this.moreNeeds.concat('wechat', 'wechat_nickname')
      const filled = fields.filter(field => {
        const value = this.info[field]
        return value !== '' && value !== null && value !== undefined
      }).length
      return filled ? '已填 ' + filled + ' 项' : '总价、用途、行业等 ' + (fields.length + 1) + ' 项'
    }
  },
  watch: {
    info: { deep: true, handler: 'scheduleDraft' },
    content: 'scheduleDraft',
    nextTime: 'scheduleDraft'
  },
  created() {
    this.load()
    window.addEventListener('pagehide', this.persistDraft)
    window.addEventListener('beforeunload', this.unload)
  },
  beforeDestroy() {
    clearTimeout(this.draftTimer)
    this.persistDraft()
    window.removeEventListener('pagehide', this.persistDraft)
    window.removeEventListener('beforeunload', this.unload)
  },
  methods: {
    list(field) {
      return (this.options[field] && this.options[field].list) || []
    },
    toOptions(list) {
      if (Array.isArray(list)) return list.map((label, index) => ({ value: index, label }))
      return Object.keys(list).map(key => ({
        value: isNaN(Number(key)) ? key : Number(key),
        label: list[key]
      }))
    },
    fieldLabel(field) {
      return this.options[field] ? this.options[field].name : field
    },
    fieldOptions(field) {
      return this.toOptions(this.list(field))
    },
    whenText(value) {
      return dateTimeText(String(value).replace('T', ' '))
    },
    setNext(days) {
      const date = new Date()
      date.setDate(date.getDate() + days)
      date.setHours(10, 0, 0, 0)
      const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
      this.nextTime = shifted.toISOString().slice(0, 16)
    },
    async load() {
      this.loading = true
      this.loadError = ''
      this.ready = false
      try {
        const profile = await employee(this.$store)
        let options = this.$store.state.customer.state.options
        if (!options.status) {
          const data = await this.$api.post('/api/base/options', {})
          options = data.data
          this.$store.state.customer.state.options = options
        }
        this.options = options
        if (this.creating) {
          const fresh = {}
          customerFields.forEach(field => {
            fresh[field] = ''
          })
          fresh.subjects = []
          fresh.subject_id = null
          fresh.project_id = null
          fresh.channel = 0
          fresh.total_budget_range = []
          fresh.price_acceptance_level = 3
          fresh.purpose_v2 = 3
          fresh.mobile = this.$route.params.mobile
          this.info = fresh
        } else {
          const data = await this.$api.post('/api/customer/detail', { id: this.$route.params.id })
          this.info = Object.assign({}, data.info, {
            subjects: (data.subjects || []).map(item => Number(item.id))
          })
        }
        this.info.total_budget_range = budgetValues(this.info.total_budget_range)
        const defaultFields = ['price_acceptance_level', 'purpose_v2']
        defaultFields.forEach(field => {
          if (this.info[field] === '' || this.info[field] == null) this.$set(this.info, field, 3)
        })
        this.info.come_time = inputDate(this.info.come_time)
        this.content = ''
        this.setNext(90)
        if (this.$route.params.from === 'schedule' && this.$route.query.plan === '1') {
          const date = validDay(this.$route.query.date)
          if (date) {
            if (this.$route.query.type === 'visit') this.info.come_time = date + 'T14:00'
            else this.nextTime = date + 'T10:00'
          }
        }
        this.baseline = this.snapshot()
        this.key = draftKey(
          profile.id,
          this.creating ? 'create' : 'follow',
          this.creating ? this.info.mobile : this.info.id
        )
        const draft = readDraft(this.key)
        if (draft && draft.value) {
          const saved = draft.value
          this.content = typeof saved.content === 'string' ? saved.content : ''
          this.nextTime = saved.nextTime || this.nextTime
          this.draftConflict = !!(saved.updatedAt && saved.updatedAt !== this.info.updated_at)
          if (!this.draftConflict)
            Object.keys(saved.changes || {}).forEach(field => {
              if (
                customerFields.indexOf(field) >= 0 &&
                ['id', 'mobile', 'log_content', 'log_time'].indexOf(field) < 0
              )
                this.$set(this.info, field, saved.changes[field])
            })
          this.draftRestored = true
          this.draftStatus = '已恢复草稿，保存后会自动清除'
        }
        this.ready = true
        if (this.$route.query.ai_profile) this.$nextTick(this.loadIncomingProfile)
        const aiTime = validAiTime(this.$route.query.ai_time)
        if (aiTime || this.$route.query.ai_plan === '1') {
          if (aiTime) this.applyAiTime(aiTime)
          else this.$nextTick(() => { this.nextOpen = true })
          const query = Object.assign({}, this.$route.query)
          delete query.ai_time
          delete query.ai_plan
          this.$router.replace({ path: this.$route.path, query })
        }
      } catch (e) {
        this.loadError = e.message
      } finally {
        this.loading = false
      }
    },
    snapshot() {
      const fields = {}
      customerFields.forEach(field => {
        if (
          ['id', 'log_content', 'log_time'].indexOf(field) < 0 &&
          Object.prototype.hasOwnProperty.call(this.info, field)
        )
          fields[field] = JSON.parse(JSON.stringify(this.info[field]))
      })
      return { fields, content: this.content, nextTime: this.nextTime }
    },
    scheduleDraft() {
      if (!this.ready || this.committed) return
      clearTimeout(this.draftTimer)
      this.draftTimer = setTimeout(this.persistDraft, 450)
    },
    persistDraft() {
      clearTimeout(this.draftTimer)
      if (!this.ready || this.committed || !this.key) return true
      if (!this.dirty) return removeDraft(this.key)
      const current = this.snapshot()
      const changes = {}
      Object.keys(current.fields).forEach(field => {
        if (JSON.stringify(current.fields[field]) !== JSON.stringify(this.baseline.fields[field]))
          changes[field] = current.fields[field]
      })
      const ok = writeDraft(this.key, {
        content: this.content,
        nextTime: this.nextTime,
        changes,
        updatedAt: this.info.updated_at || ''
      })
      this.draftStatus = ok
        ? '草稿已保留 · 仅当前账号在此设备可恢复'
        : '此浏览器无法保留草稿，请及时保存'
      return ok
    },
    unload(event) {
      if (!this.persistDraft() && this.dirty) {
        event.preventDefault()
        event.returnValue = ''
      }
    },
    async discardDraft() {
      try {
        await this.$dialog.confirm({
          title: '丢弃草稿',
          message: '清除当前未保存的修改，恢复刚打开客户时的资料？'
        })
      } catch (e) {
        return
      }
      Object.keys(this.baseline.fields).forEach(key =>
        this.$set(this.info, key, JSON.parse(JSON.stringify(this.baseline.fields[key])))
      )
      this.content = this.baseline.content
      this.nextTime = this.baseline.nextTime
      removeDraft(this.key)
      this.draftRestored = false
      this.draftConflict = false
      this.draftStatus = '填写内容会自动保留为草稿'
      this.profileUndo = null
    },
    applyKeyword(data) {
      this.info.keyword = data.keyword
    },
    applyAiProfile(data) {
      if (this.saving || !this.ready || !data || !Array.isArray(data.updates) || !data.updates.length || data.updates.length > profileFields.length) return
      const seen = {}
      const valid = data.updates.every(update => {
        const field = update.field
        if (profileFields.indexOf(field) < 0 || seen[field] || !data.expected || !Object.prototype.hasOwnProperty.call(data.expected, field)) return false
        seen[field] = true
        const value = profileValue(field, update.value, this.profileChoices[field].options)
        return value !== null && sameProfileValue(this.info[field], data.expected[field])
      })
      if (!valid) return this.$toast('画像选项已有变化，请重新核对后填入')
      const before = {}, after = {}
      data.updates.forEach(update => {
        before[update.field] = this.info[update.field] == null ? '' : JSON.parse(JSON.stringify(this.info[update.field]))
        after[update.field] = profileValue(update.field, update.value, this.profileChoices[update.field].options)
      })
      this.profileUndo = { before, after }
      data.updates.forEach(update => this.$set(this.info, update.field, after[update.field]))
      if (data.updates.some(update => this.moreNeeds.indexOf(update.field) >= 0)) this.more = true
      this.incomingProfile = null
      this.$toast('已填入 ' + data.updates.length + ' 项画像，请核对后保存')
    },
    undoAiProfile() {
      if (!this.profileUndo || this.saving) return
      Object.keys(this.profileUndo.after).forEach(field => {
        if (sameProfileValue(this.info[field], this.profileUndo.after[field])) this.$set(this.info, field, this.profileUndo.before[field])
      })
      this.profileUndo = null
      this.$toast('已撤销画像填入，手动修改的内容已保留')
    },
    async loadIncomingProfile() {
      if (this.creating || this.incomingLoading || !this.$route.query.ai_profile) return
      const id = String(this.$route.query.ai_profile)
      const fields = String(this.$route.query.ai_fields || '').split(',').filter(field => profileFields.indexOf(field) >= 0)
      if (!/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id) || !fields.length) return
      this.incomingLoading = true
      this.incomingError = ''
      try {
        const data = await this.$api.post('/api/ai/customer-analysis/detail', { customer_id: this.info.id, analysis_id: id, content: this.content, profile: this.analysisProfile })
        if (this._isDestroyed) return
        this.incomingProfile = data
        this.incomingFields = fields
        const query = Object.assign({}, this.$route.query)
        delete query.ai_profile
        delete query.ai_fields
        this.$router.replace({ path: this.$route.path, query })
      } catch (e) { if (!this._isDestroyed) this.incomingError = e.message }
      finally { this.incomingLoading = false }
    },
    applyAiTime(value) {
      if (!value) { this.nextOpen = true; return }
      const time = validAiTime(value)
      if (!time) return this.$toast('建议时间已过，请重新确认')
      this.nextTime = time
      this.$toast('已填入下次跟进时间，保存后生效')
    },
    async save() {
      if (this.saving || !this.ready) return
      this.error = ''
      if (!this.content.trim()) this.error = '请先填写本次跟进内容'
      else if (!this.info.name || !this.info.name.trim()) this.error = '请填写客户姓名'
      else if (this.creating && !canCall(this.info.mobile)) this.error = '请输入正确的 11 位手机号'
      else if (this.creating && !Number(this.info.source_channel_v2))
        this.error = '请选择客户来源'
      else if (!budgetValues(this.info.total_budget_range).length) this.error = '请选择总价预算范围'
      else if (!this.info.keyword || !this.info.keyword.trim()) this.error = '请填写客户关键词'
      else if (!this.info.status || Number(this.info.status) === -2)
        this.error = '请选择本次跟进后的客户状态'
      else if (!this.info.labels) this.error = '请选择意向等级'
      else if (!this.nextTime || isNaN(new Date(this.nextTime).getTime()))
        this.error = '请设置有效的下次跟进时间'
      if (this.error) {
        this.$toast(this.error)
        return
      }
      this.saving = true
      try {
        await this.$dialog.confirm({
          title: this.creating ? '创建客户' : '保存本次跟进',
          message: '确认记录本次沟通，并更新下次跟进安排？'
        })
        const data = payload(this.info, this.content, this.nextTime)
        data.come_time = data.come_time
          ? data.come_time.replace('T', ' ') + (data.come_time.length === 16 ? ':00' : '')
          : ''
        if (!this.creating && !canCall(data.mobile)) delete data.mobile
        const saved = await this.$api.post(this.creating ? '/api/customer/create' : '/api/customer/edit', {
          customer: data,
          ai_draft_key: this.creating ? this.key : undefined
        })
        this.committed = true
        clearTimeout(this.draftTimer)
        removeDraft(this.key)
        if (this.creating) this.$store.commit('refreshData')
        else this.$store.commit('customerChanged', { id: this.info.id, info: saved && saved.info, kind: 'followup' })
        this.$toast(this.creating ? '客户已创建，跟进已保存' : '跟进已保存')
        const from = this.$route.params.from
        if (from === 'schedule') {
          const date =
            this.$route.query.plan === '1'
              ? (this.$route.query.type === 'visit' ? this.info.come_time : this.nextTime).slice(
                  0,
                  10
                )
              : this.$route.query.date
          this.$router.replace(scheduleLocation(date, this.$route.query.type))
        } else if (from === 'daily-brief') {
          this.$router.replace({ path: '/daily-brief', query: { date: this.$route.query.date } })
        } else {
          returnToCustomerOrigin(this,
            from === 'remind' ? '/remind' : from === 'workbench' ? '/workbench' : '/customer'
          )
        }
      } catch (e) {
        if (e && e.message) {
          this.error = e.message
          this.persistDraft()
        }
      } finally {
        this.saving = false
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/customer-form.vue